/*
 * Proprietary and Confidential
 *
 * Copyright (c) [2018] -  [] Marcelo H. Krebber - European Union 2018
 * All Rights Reserved.
 *
 * Dissemination or reproduction of this file [billboard.js] or parts within
 * via any medium is strictly forbidden unless prior written permission is obtained
 * from <marcelo.krebber@gmail.com>
 *
 * Last modified: 26.09.18 21:01
 */

import moment from 'moment';

import React, {useState} from 'react';
import {useDispatch} from 'react-redux';
import {asyncCreatePost,} from '../../actions/index';
import {localUpdateMedia} from '../../actions/spaces';
import {showForceVisibleImages, showVisibleImages} from "../../actions/image-handler";
import MediaRichEditor from '../media-rich-editor/media-rich-editor';
import {useAuthorization, useGenericData, useIsHomeSpaceLocation} from "../../selectors";
import TimelinePost from "./timeline-post";
import NativeScroller from "../scrollbars/native-scroller";
import useAsyncFetchPosts from "../hooks/async-fetch-posts";
import {SpinnerBig} from "../util/spinner";
import {uploadFilesAsyncWorker} from "../dropzone-uploader/file-uploader";
import {getPostsUploadUrl} from "../../actions/environment";
import {getVisibilityFromLocalStorage} from "../../actions/local-storage";
import VideoReelContainer from "../video-reel/video-reel-container";

const ONE_MINUTE = 1000 * 60;

// component uses native onscroll-stop implementation
const Timeline = ({username, spacename, spaceId, configuration}) => {
    const [isUploading, setIsUploading] = useState(false);
    const [fetchPage, setFetchPage] = useState({next: 0, first: true, last: false, moment: moment()});
    const {authorization, isAuthorized, isSuperUser, isTransitioning} = useAuthorization();
    const {posts, status, page} = useAsyncFetchPosts(username, spacename, fetchPage);
    const {genericdata} = useGenericData();
    const {isHomeSpace, pathname} = useIsHomeSpaceLocation();
    const dispatch = useDispatch();

    const uploadFiles = async (username, spacename, text, files, visibility, access) => {

        setIsUploading(true);
        const mediaUploadUrl = getPostsUploadUrl(username);
        const media= await uploadFilesAsyncWorker(mediaUploadUrl, files);

        dispatch(asyncCreatePost(username, {
                title: '',
                text: text,
                media: media,
                visibility: visibility, access: access},
            spacename, post => {
            setIsUploading(false);
                dispatch(localUpdateMedia(post.media));
                // refresh uploaded media
                showForceVisibleImages();
            })
        );
    }

    // other vars: elem.scrollHeight, elem.scrollTop, elem.clientHeight, elem.scrollTop + elem.clientHeight
    const onScrollStop = (event) => {
        const {next, last, first, when} = page;
        const {scrollTop, clientHeight, scrollHeight} = event.target;
        showVisibleImages();

        /* fetch next page of posts if outside visible area plus threshold */
        if (!last && (scrollTop + clientHeight + 100 >= scrollHeight)) {
            setFetchPage(page)

        } else if (scrollTop === 0 && (!first || moment().diff(when) > ONE_MINUTE)) {
            /* reset to first page of posts */
            setFetchPage({...page, next: 0})
        }
    }

    const uploadEmbeddedVideo = (username, spacename, text, embedded, visibility, access) => {
        dispatch(asyncCreatePost(username, {
                title: '',
                text: text,
                media: embedded,
                visibility: visibility, access: access},
            spacename, post => {
            showForceVisibleImages();
        }));
    }

    const handleTextAreaEnter = (text, files, embedded) => {
        const authName = authorization.principal.username;
        const {visibility, access} = getVisibilityFromLocalStorage(isHomeSpace);

        if (embedded.length > 0) {
            uploadEmbeddedVideo(isSuperUser ? username : authName,
                spacename,
                text,
                embedded,
                visibility, access);
        } else {
            uploadFiles(isSuperUser ? username : authName,
                spacename,
                text,
                files,
                visibility, access);
        }
    }

    const renderPosts = (authName, username, posts, spacename) => {
        return (posts.map((post, idx) => {
                return <TimelinePost key={idx} authname={authName} post={post} username={username}
                                     spacename={spacename}/>
            })
        );
    }

    if (isTransitioning) return null;
    const authName = authorization.principal.username;
    const isEditable = (username === authName)
        || (genericdata && genericdata.isMember && spacename !== "home")
        || isSuperUser;

    return <div className='timeline-container'>
        <NativeScroller onScrollStop={onScrollStop}>
            <div className={isEditable ? 'card-columns' : 'd-none'}>
                {isAuthorized && isEditable && <div className='card'>
                    <MediaRichEditor className='billboard-upload'
                                     id={spaceId}
                                     username={isSuperUser ? username : authName}
                                     callback={handleTextAreaEnter}/>
                </div>}
            </div>

            {isUploading && <SpinnerBig/>}

            {posts && <div className="pb-2 px-1">
                <VideoReelContainer posts={posts}/>
            </div>}

            {posts && <div className='card-columns'>
                {renderPosts(authName, username, posts, spacename)}
                {status === 'pending' && <div>
                    <SpinnerBig/>
                </div>}
            </div>}
        </NativeScroller>

    </div>
}

export default Timeline;